// BCM-898 · PA6 — Stat and Delivery Date Variance (slide 20).
// Rendered as its OWN stacked section, independent of the "Consumed Batch
// Variance/Mismatch" tab, only when statDateVariancePresent is true and the
// applicable direction's BCM-744 update type requires action (Manual or
// Automatic after approval — Automatic without approval hides the section).
// Sync PO only — no Sync Inventory, no Resubmit Document, and no second/
// View-Document tab, because the statistical date comes from SAP and not from
// a document. Two-card layout per the mockup: "SAP PO Values" (read-only
// context) + "Action Required" (the editable/read-only date + Approve).
//
// Multiple Variance/Mismatch Scenario (design slides 24-25): when more than
// one variance section is present, they stack one below the other and share
// ONE common Approve button, rendered as its own block below ALL the sections
// (by PA6VarianceMismatch) — not nested inside any single section's "Action
// Required" box. So this component only renders the read-only/editable
// fields; the edited date lives in PA6VarianceMismatch (value/onChange) and
// Approve/validation/payload/toast live there too.
import React from "react";
import { Typography, Box } from "@material-ui/core";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import moment from "moment";
import ActionTabs from "../../components/common/ActionTabs";
import SyncActions from "../../components/common/SyncActions";
import CustomDatePickerMui from "../../../models/inputs/DateInput";
import { useGRStyles } from "../../goodsReceiptStyles";
import { formatDate } from "../../utils/formatters";

export const isEarlyDateVariance = (row) => {
  const statDate = row && row.statisticalDate ? new Date(row.statisticalDate) : null;
  return statDate ? statDate.getTime() > Date.now() : false;
};

// Gate used by PA6VarianceMismatch to decide whether to mount this section.
export const shouldShowStatDateVariance = (row) => {
  if (!row || row.statDateVariancePresent !== true) return false;
  const early = isEarlyDateVariance(row);
  return early
    ? row.earlyDateUpdateType === "Manual" || row.earlyDateUpdateType === "Automatic after approval"
    : row.laterDateUpdateType === "Manual" || row.laterDateUpdateType === "Automatic after approval";
};

// "Manual" → calendar editable; "Automatic after approval" → calendar read-only.
export const isStatDateEditable = (row) => {
  const early = isEarlyDateVariance(row);
  return early ? row.earlyDateUpdateType === "Manual" : row.laterDateUpdateType === "Manual";
};

export const isStatDateAutoAfterApproval = (row) => {
  const early = isEarlyDateVariance(row);
  return early
    ? row.earlyDateUpdateType === "Automatic after approval"
    : row.laterDateUpdateType === "Automatic after approval";
};

const InfoField = ({ classes, label, value, orange }) => (
  <div>
    <div className={classes.fieldLabel}>{label}</div>
    <div className={classes.fieldValue} style={orange ? { color: "#E36E39" } : undefined}>{value == null || value === "" ? "-" : value}</div>
  </div>
);

// Fixed 3-column grid so the two rows line up exactly, with a divider between
// them — matches the mockup's "SAP PO Values" card precisely.
const infoGridStyle = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px 16px" };

const StatDateVarianceSection = ({ row, detail, featuresAccess, value, onChange, onRefresh }) => {
  const classes = useGRStyles();
  const early = isEarlyDateVariance(row);
  const editable = isStatDateEditable(row);

  const actualVarianceDays = row.statisticalDate
    ? moment().startOf("day").diff(moment(row.statisticalDate).startOf("day"), "days")
    : null;
  const varianceLabel = actualVarianceDays != null ? `${Math.abs(actualVarianceDays)} days ${early ? "early" : "late"}` : "-";

  const render = () => (
    <React.Fragment>
      {actualVarianceDays != null && (
        <Box display="flex" alignItems="center" style={{ gap: 6, marginBottom: 8 }}>
          <span style={{ color: "#C0392B", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, border: "1px solid #C0392B", borderRadius: 12, padding: "2px 10px", fontSize: "0.75rem" }}>
            <WarningRoundedIcon style={{ fontSize: 16 }} />
            {Math.abs(actualVarianceDays)} DAYS {early ? "EARLY" : "LATE"}
          </span>
        </Box>
      )}
      <Typography className={classes.sectionSub} style={{ marginBottom: 12 }}>
        Goods receipt date falls outside the allowed variance window for this PO. Set the corrected statistical date and approve to update SAP.
      </Typography>

      <Box display="flex" style={{ gap: 16, alignItems: "stretch" }} flexWrap="wrap">
        {/* SAP PO Values — read-only context, matches the mockup's left card. */}
        <Box flex="1 1 320px" style={{ border: "1px solid #E0E4EA", borderRadius: 8, padding: "14px 18px" }}>
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 12 }}>SAP PO Values</Typography>
          <Box style={infoGridStyle}>
            <InfoField classes={classes} label="Statistical Date" value={formatDate(row.statisticalDate)} />
            <InfoField classes={classes} label="Delivery Date" value={formatDate(row.deliveryDate)} />
            <InfoField classes={classes} label="Present Date" value={formatDate(new Date())} />
          </Box>
          <Box style={{ borderTop: "1px solid #E0E4EA", margin: "12px 0" }} />
          <Box style={infoGridStyle}>
            <InfoField classes={classes} label="Early Variance Limit" value={row.earlyDateVarianceLimit ?? row.acceptanceLimitForEarlyDeliveryDateChangeByCMO ?? "-"} />
            <InfoField classes={classes} label="Later Variance Limit" value={row.laterDateVarianceLimit ?? row.acceptanceLimitForLaterDeliveryDateChangeByCMO ?? "-"} />
            <InfoField classes={classes} label="Actual Variance" value={varianceLabel} orange />
          </Box>
        </Box>

        {/* Action Required — the editable/read-only date. The Approve button
            is NOT rendered here — it's a separate, common block below every
            stacked section (see PA6VarianceMismatch). */}
        <Box flex="1 1 260px" style={{ border: "1px solid #E36E39", borderRadius: 8, padding: "14px 18px" }}>
          <Typography style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: 12 }}>Action Required</Typography>
          <div className={classes.fieldLabel} style={{ marginBottom: 6 }}>
            Update SAP PO Stat Date To <span style={{ color: "red" }}>*</span>
          </div>
          {editable ? (
            <CustomDatePickerMui
              name="updateStatDate"
              value={value}
              onChange={onChange}
              size="small"
            />
          ) : (
            // Automatic after approval, early direction: show the Present
            // Date (matches what's actually sent on Approve in
            // PA6VarianceMismatch), not the stale SAP statisticalDate.
            // Later direction is unchanged — echoes statisticalDate.
            <Typography style={{ fontWeight: 700 }}>
              {formatDate(early ? new Date() : row.statisticalDate)}
            </Typography>
          )}
          <Typography variant="caption" style={{ display: "block", color: "#6B7280", marginTop: 6, fontStyle: "italic" }}>
            Approving writes this date back to the PO in SAP and clears the variance.
          </Typography>
        </Box>
      </Box>
    </React.Fragment>
  );

  return (
    <ActionTabs
      headRight={
        <SyncActions row={row} detail={detail} showSyncPO resetsBatchPO resetsBatchInventory onRefresh={onRefresh} />
      }
      tabs={[{ key: "statdate", label: "Stat and Delivery Date Variance", badge: 1, render }]}
    />
  );
};

export default StatDateVarianceSection;
